import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { API, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/common/StatusBadge";
import {
  Loader2, Lock, Send, CheckCircle2, XCircle, Upload, FileText, Plus, Building2, User, FileSignature, Hotel, CalendarClock, Download,
} from "lucide-react";

const SALES_TYPES = ["Rooms", "Restaurant", "Banquet", "All"];
const PLANS = [["ep", "EP · European Plan"], ["cp", "CP · Continental Plan"], ["map", "MAP · Modified American Plan"], ["ap", "AP · American Plan"]];

const emptyPlans = () => ({
  ep: { enabled: false, single_rate: "", double_rate: "" },
  cp: { enabled: false, single_rate: "", double_rate: "" },
  map: { enabled: false, single_rate: "", double_rate: "" },
  ap: { enabled: false, single_rate: "", double_rate: "" },
});

const normalizePlans = (t) => {
  const base = emptyPlans();
  if (t && t.plans) {
    for (const k of ["ep", "cp", "map", "ap"]) {
      if (t.plans[k]) base[k] = { enabled: !!t.plans[k].enabled, single_rate: t.plans[k].single_rate || "", double_rate: t.plans[k].double_rate || "" };
    }
  } else if (t) {
    for (const k of ["ep", "cp", "map", "ap"]) {
      if (t[k]) base[k] = { enabled: true, single_rate: t.single_rate || "", double_rate: t.double_rate || "" };
    }
  }
  return base;
};

const EMPTY = {
  company_name: "", state: "", district: "", area: "", full_address: "", gst_number: "",
  type_of_sales: [], poc_name: "", poc_designation: "", poc_contact: "", poc_email: "",
  contract_status: "Non-Contracted", agreement_start: "", agreement_end: "", tariff_in_innkeys: false,
  sales_status: false, remarks: "", tariffs: [], follow_up_datetime: "", meeting_logs: [],
};

function Section({ id, icon: Icon, title, right, children }) {
  return (
    <div data-testid={id} className="rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary grid place-items-center"><Icon className="h-4 w-4" /></div>
          <h3 className="text-base font-semibold">{title}</h3>
        </div>
        {right}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

const Field = ({ label, children, required }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}{required && <span className="text-destructive"> *</span>}</Label>
    {children}
  </div>
);

export default function CorporateDetailModal({ open, corporateId, createMode, properties, onClose, onSaved }) {
  const { isManagement, user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(!createMode);
  const [saving, setSaving] = useState(false);
  const [tariffStatus, setTariffStatus] = useState("Draft");
  const [rejectionNote, setRejectionNote] = useState("");
  const [agreementFile, setAgreementFile] = useState(null);
  const [salesMonth, setSalesMonth] = useState("");
  const [uploading, setUploading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectText, setRejectText] = useState("");

  const approvedSE = !isManagement && tariffStatus === "Approved";

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => { api.get("/locations/states").then((r) => setStates(r.data.states)); }, []);
  useEffect(() => {
    if (form.state) api.get("/locations/districts", { params: { state: form.state } }).then((r) => setDistricts(r.data.districts));
  }, [form.state]);

  const buildTariffs = (existing) => {
    return properties.map((p) => {
      const found = (existing || []).find((t) => t.property_id === p.id);
      return { property_id: p.id, property_name: p.name, plans: normalizePlans(found), payment_terms: found?.payment_terms || "", office_pickup_drop: !!found?.office_pickup_drop, laundry: !!found?.laundry, laundry_remarks: found?.laundry_remarks || "" };
    });
  };

  useEffect(() => {
    if (createMode) {
      setForm({ ...EMPTY, tariffs: buildTariffs([]) });
      setTariffStatus("Draft");
      setLoading(false);
      return;
    }
    setLoading(true);
    api.get(`/corporates/${corporateId}`).then((r) => {
      const d = r.data;
      setForm({
        company_name: d.company_name || "", state: d.state || "", district: d.district || "", area: d.area || "",
        full_address: d.full_address || "", gst_number: d.gst_number || "", type_of_sales: d.type_of_sales || [],
        poc_name: d.poc_name || "", poc_designation: d.poc_designation || "", poc_contact: d.poc_contact || "",
        poc_email: d.poc_email || "", contract_status: d.contract_status || "Non-Contracted",
        agreement_start: d.agreement_start || "", agreement_end: d.agreement_end || "",
        tariff_in_innkeys: !!d.tariff_in_innkeys, sales_status: !!d.sales_status, remarks: d.remarks || "",
        tariffs: buildTariffs(d.tariffs), follow_up_datetime: d.follow_up_datetime || "", meeting_logs: d.meeting_logs || [],
      });
      setTariffStatus(d.tariff_status || "Draft");
      setRejectionNote(d.tariff_rejection_note || "");
      setAgreementFile(d.agreement_file || null);
      setSalesMonth(d.sales_month || "");
    }).catch((e) => { toast.error(apiError(e)); onClose(); }).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [corporateId, createMode, properties]);

  const toggleSales = (t) => {
    setForm((p) => ({ ...p, type_of_sales: p.type_of_sales.includes(t) ? p.type_of_sales.filter((x) => x !== t) : [...p.type_of_sales, t] }));
  };

  const setTariff = (idx, k, v) => {
    setForm((p) => {
      const tariffs = [...p.tariffs];
      tariffs[idx] = { ...tariffs[idx], [k]: v };
      return { ...p, tariffs };
    });
  };

  const setPlan = (idx, planKey, field, v) => {
    setForm((p) => {
      const tariffs = [...p.tariffs];
      const t = { ...tariffs[idx] };
      t.plans = { ...t.plans, [planKey]: { ...t.plans[planKey], [field]: v } };
      tariffs[idx] = t;
      return { ...p, tariffs };
    });
  };

  const validate = () => {
    const req = { company_name: "Company Name", state: "State", district: "District", area: "Area", full_address: "Address", poc_name: "POC Name", poc_contact: "POC Contact", poc_email: "Email" };
    for (const [k, label] of Object.entries(req)) {
      if (!form[k] || !String(form[k]).trim()) { toast.error(`${label} is required`); return false; }
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    const payload = { ...form, agreement_start: form.agreement_start || null, agreement_end: form.agreement_end || null, follow_up_datetime: form.follow_up_datetime || null };
    try {
      if (createMode) {
        const r = await api.post("/corporates", payload);
        toast.success(r.data.tariff_status === "Pending" ? "Corporate created — tariff sent for management approval" : "Corporate created");
        onClose();
        onSaved();
        return;
      } else {
        const r = await api.put(`/corporates/${corporateId}`, payload);
        const resent = !isManagement && r.data.tariff_status === "Pending" && tariffStatus !== "Pending";
        setTariffStatus(r.data.tariff_status);
        setRejectionNote(r.data.tariff_rejection_note || "");
        toast.success(resent ? "Changes saved — tariff sent for management approval" : "Changes saved");
        onClose();
        onSaved();
        return;
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const submitTariff = async () => {
    try {
      await api.put(`/corporates/${corporateId}`, { ...form, agreement_start: form.agreement_start || null, agreement_end: form.agreement_end || null, follow_up_datetime: form.follow_up_datetime || null });
      const r = await api.post(`/corporates/${corporateId}/submit-tariff`);
      setTariffStatus(r.data.tariff_status);
      toast.success("Tariff submitted for management approval");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  const approve = async () => {
    try {
      const r = await api.post(`/corporates/${corporateId}/approve-tariff`);
      setTariffStatus(r.data.tariff_status);
      setRejectionNote("");
      toast.success("Tariff approved");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  const reject = async () => {
    try {
      const r = await api.post(`/corporates/${corporateId}/reject-tariff`, { note: rejectText });
      setTariffStatus(r.data.tariff_status);
      setRejectionNote(rejectText);
      setRejectOpen(false);
      setRejectText("");
      toast.success("Tariff rejected");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadAgreement = async (file) => {
    if (!file) return;
    if (createMode) { toast.error("Save the corporate first, then upload the agreement"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post(`/corporates/${corporateId}/agreement`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAgreementFile(r.data);
      toast.success("Agreement uploaded");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };

  const downloadAgreement = async () => {
    try {
      const res = await api.get(`/files/${agreementFile.path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = agreementFile.filename || "agreement";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const addMeetingLog = () => {
    if (form.meeting_logs.length >= 4) { toast.error("Maximum 4 meeting logs allowed"); return; }
    setForm((p) => ({ ...p, meeting_logs: [...p.meeting_logs, { date: new Date().toISOString().slice(0, 10), summary: "", next_step: "", logged_by: user.name }] }));
  };
  const setLog = (i, k, v) => setForm((p) => { const l = [...p.meeting_logs]; l[i] = { ...l[i], [k]: v }; return { ...p, meeting_logs: l }; });
  const removeLog = (i) => setForm((p) => ({ ...p, meeting_logs: p.meeting_logs.filter((_, idx) => idx !== i) }));

  const canSubmitTariff = !isManagement && !createMode && (tariffStatus === "Draft" || tariffStatus === "Rejected");
  const inputDisabled = false; // master fields always editable per spec

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center justify-between gap-4">
            <div>
              <DialogTitle className="font-serif text-2xl">{createMode ? "New Corporate" : form.company_name || "Corporate"}</DialogTitle>
              <DialogDescription className="text-xs mt-0.5">
                {createMode ? "Create a new corporate master record." : `Active Month: ${salesMonth} · Tariff: `}
                {!createMode && <StatusBadge status={tariffStatus} />}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="h-80 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="p-6 space-y-5">
            {/* 1. Company Profile */}
            <Section id="section-company-profile" icon={Building2} title="1. Company Profile">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Company Name" required><Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="field-company-name" /></Field>
                <Field label="GST Number"><Input value={form.gst_number} onChange={(e) => set("gst_number", e.target.value.toUpperCase())} placeholder="22AAAAA0000A1Z5" data-testid="field-gst" /></Field>
                <Field label="State" required>
                  <Select value={form.state} onValueChange={(v) => { set("state", v); set("district", ""); }}>
                    <SelectTrigger data-testid="field-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                    <SelectContent>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="District" required>
                  <Select value={form.district} onValueChange={(v) => set("district", v)} disabled={!form.state}>
                    <SelectTrigger data-testid="field-district"><SelectValue placeholder="Select district" /></SelectTrigger>
                    <SelectContent>{districts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Area / Sub-location" required><Input value={form.area} onChange={(e) => set("area", e.target.value)} data-testid="field-area" /></Field>
                <div className="sm:col-span-2">
                  <Field label="Full Address" required><Textarea value={form.full_address} onChange={(e) => set("full_address", e.target.value)} rows={2} data-testid="field-address" /></Field>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Type of Sales</Label>
                  <div className="flex flex-wrap gap-4 mt-2">
                    {SALES_TYPES.map((t) => (
                      <label key={t} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={form.type_of_sales.includes(t)} onCheckedChange={() => toggleSales(t)} data-testid={`field-sales-type-${t.toLowerCase()}`} /> {t}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </Section>

            {/* 2. POC */}
            <Section id="section-poc-details" icon={User} title="2. Point of Contact">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="POC Name" required><Input value={form.poc_name} onChange={(e) => set("poc_name", e.target.value)} data-testid="field-poc-name" /></Field>
                <Field label="Designation"><Input value={form.poc_designation} onChange={(e) => set("poc_designation", e.target.value)} data-testid="field-poc-designation" /></Field>
                <Field label="Contact Number" required><Input value={form.poc_contact} onChange={(e) => set("poc_contact", e.target.value)} placeholder="9876543210" data-testid="field-poc-contact" /></Field>
                <Field label="Email ID" required><Input type="email" value={form.poc_email} onChange={(e) => set("poc_email", e.target.value)} data-testid="field-poc-email" /></Field>
              </div>
            </Section>

            {/* 3. Contract & Operational */}
            <Section id="section-contract-status" icon={FileSignature} title="3. Contract & Operational Status">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="text-xs">Status</Label>
                  <RadioGroup value={form.contract_status} onValueChange={(v) => set("contract_status", v)} className="flex gap-6 mt-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer"><RadioGroupItem value="Contracted" data-testid="field-contracted" /> Contracted</label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer"><RadioGroupItem value="Non-Contracted" data-testid="field-non-contracted" /> Non-Contracted</label>
                  </RadioGroup>
                </div>
                {form.contract_status === "Contracted" && (
                  <>
                    <Field label="Agreement Start Date"><Input type="date" value={form.agreement_start || ""} onChange={(e) => set("agreement_start", e.target.value)} data-testid="field-agreement-start" /></Field>
                    <Field label="Agreement End Date"><Input type="date" value={form.agreement_end || ""} onChange={(e) => set("agreement_end", e.target.value)} data-testid="field-agreement-end" /></Field>

                    <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
                      <div><div className="text-sm font-medium">Tariff added in Inn Keys</div><div className="text-xs text-muted-foreground">Loaded into PMS</div></div>
                      <Switch checked={form.tariff_in_innkeys} onCheckedChange={(v) => set("tariff_in_innkeys", v)} data-testid="field-innkeys" />
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
                      <div><div className="text-sm font-medium">Sales in {salesMonth || "Active Month"}</div><div className="text-xs text-muted-foreground">Revenue booked this month</div></div>
                      <Switch checked={form.sales_status} onCheckedChange={(v) => set("sales_status", v)} data-testid="field-sales-status" />
                    </div>

                    <div className="sm:col-span-2">
                      <Label className="text-xs">Agreement Copy (PDF / Image · max 10MB)</Label>
                      <div className="mt-2 flex flex-wrap items-center gap-3">
                        <label className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-dashed border-primary/40 bg-accent/50 cursor-pointer hover:border-primary transition">
                          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
                          <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => uploadAgreement(e.target.files[0])} data-testid="field-agreement-upload" />
                        </label>
                        {agreementFile && (
                          <button type="button" onClick={downloadAgreement} className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-border bg-card hover:border-primary" data-testid="agreement-download">
                            <FileText className="h-4 w-4 text-primary" /> {agreementFile.filename} <Download className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </>
                )}

                <div className="sm:col-span-2">
                  <Field label="Remarks"><Textarea value={form.remarks} onChange={(e) => set("remarks", e.target.value)} rows={2} data-testid="field-remarks" /></Field>
                </div>
              </div>
            </Section>

            {/* 4. Hotel-wise Tariffs */}
            {form.contract_status === "Contracted" && (
            <Section
              id="section-hotel-tariffs"
              icon={Hotel}
              title="4. Hotel-wise Tariff Details"
              right={<StatusBadge status={tariffStatus} testid="tariff-approval-status-badge" />}
            >
              {approvedSE && (
                <div data-testid="tariff-resubmit-notice" className="mb-4 flex items-center gap-2 text-sm rounded-lg border border-dashed border-amber-300 bg-amber-50 text-amber-800 px-4 py-3">
                  <Lock className="h-4 w-4" /> Approved by Management. Any tariff change you save will be sent for approval again.
                </div>
              )}
              {tariffStatus === "Pending" && (
                <div className="mb-4 flex items-center gap-2 text-sm rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3">
                  <CalendarClock className="h-4 w-4" /> Awaiting management approval.
                </div>
              )}
              {tariffStatus === "Rejected" && rejectionNote && (
                <div className="mb-4 flex items-start gap-2 text-sm rounded-lg border border-rose-200 bg-rose-50 text-rose-800 px-4 py-3">
                  <XCircle className="h-4 w-4 mt-0.5" /> <div><strong>Rejected:</strong> {rejectionNote}</div>
                </div>
              )}

              <div className="space-y-4">
                {form.tariffs.map((t, idx) => (
                  <div key={t.property_id} className="rounded-lg border border-border p-4" data-testid={`tariff-hotel-${idx}`}>
                    <div className="font-semibold text-sm mb-3 flex items-center gap-2"><Hotel className="h-4 w-4 text-primary" /> {t.property_name}</div>
                    <p className="text-xs text-muted-foreground mb-2">Select a plan to enter its occupancy rates.</p>
                    <div className="space-y-2">
                      {PLANS.map(([k, lbl]) => {
                        const plan = t.plans?.[k] || { enabled: false, single_rate: "", double_rate: "" };
                        return (
                          <div key={k} className={`rounded-lg border p-3 transition-colors ${plan.enabled ? "border-primary/40 bg-accent/40" : "border-border"}`}>
                            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                              <Checkbox checked={plan.enabled} onCheckedChange={(v) => setPlan(idx, k, "enabled", !!v)} data-testid={`tariff-plan-${k}-${idx}`} /> {lbl}
                            </label>
                            {plan.enabled && (
                              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                                <Field label="Single Occupancy (₹)"><Input value={plan.single_rate} onChange={(e) => setPlan(idx, k, "single_rate", e.target.value)} data-testid={`tariff-${k}-single-${idx}`} /></Field>
                                <Field label="Double Occupancy (₹)"><Input value={plan.double_rate} onChange={(e) => setPlan(idx, k, "double_rate", e.target.value)} data-testid={`tariff-${k}-double-${idx}`} /></Field>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-3">
                      <Field label="Payment Terms"><Input value={t.payment_terms} onChange={(e) => setTariff(idx, "payment_terms", e.target.value)} data-testid={`tariff-payment-${idx}`} /></Field>
                    </div>
                    <div className="mt-3 grid sm:grid-cols-2 gap-3">
                      <label className={`flex items-center gap-2 text-sm font-medium cursor-pointer rounded-lg border p-3 transition-colors ${t.office_pickup_drop ? "border-primary/40 bg-accent/40" : "border-border"}`}>
                        <Checkbox checked={t.office_pickup_drop} onCheckedChange={(v) => setTariff(idx, "office_pickup_drop", !!v)} data-testid={`tariff-pickup-drop-${idx}`} /> Office Pickup &amp; Drop
                      </label>
                      <label className={`flex items-center gap-2 text-sm font-medium cursor-pointer rounded-lg border p-3 transition-colors ${t.laundry ? "border-primary/40 bg-accent/40" : "border-border"}`}>
                        <Checkbox checked={t.laundry} onCheckedChange={(v) => setTariff(idx, "laundry", !!v)} data-testid={`tariff-laundry-${idx}`} /> Laundry
                      </label>
                    </div>
                    {t.laundry && (
                      <div className="mt-3">
                        <Field label="Laundry Remarks"><Input value={t.laundry_remarks} placeholder="e.g. 2 pieces per day complimentary" onChange={(e) => setTariff(idx, "laundry_remarks", e.target.value)} data-testid={`tariff-laundry-remarks-${idx}`} /></Field>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {canSubmitTariff && (
                  <Button onClick={submitTariff} variant="secondary" data-testid="request-tariff-approval-button">
                    <Send className="h-4 w-4 mr-2" /> Submit Tariff for Approval
                  </Button>
                )}
                {isManagement && !createMode && (
                  <>
                    <Button onClick={approve} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-tariff-button"><CheckCircle2 className="h-4 w-4 mr-2" /> Approve</Button>
                    <Button onClick={() => setRejectOpen(true)} variant="outline" className="text-destructive border-destructive/40" data-testid="reject-tariff-button"><XCircle className="h-4 w-4 mr-2" /> Reject</Button>
                  </>
                )}
              </div>

              {rejectOpen && (
                <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50/60 p-4 space-y-2">
                  <Label className="text-xs">Rejection reason</Label>
                  <Textarea value={rejectText} onChange={(e) => setRejectText(e.target.value)} rows={2} placeholder="Explain what needs to change…" data-testid="reject-note-input" />
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => setRejectOpen(false)}>Cancel</Button>
                    <Button size="sm" className="bg-destructive hover:bg-destructive/90" onClick={reject} data-testid="confirm-reject-button">Confirm Reject</Button>
                  </div>
                </div>
              )}
            </Section>
            )}

            {/* 5. Meeting Logs */}
            <Section
              id="section-meeting-logs"
              icon={CalendarClock}
              title="5. Meeting Logs & Follow-up"
              right={<span className="text-xs font-mono text-muted-foreground">{form.meeting_logs.length}/4 logged</span>}
            >
              <Field label="Next Follow-up Date & Time">
                <Input type="datetime-local" value={form.follow_up_datetime || ""} onChange={(e) => set("follow_up_datetime", e.target.value)} className="max-w-xs" data-testid="field-followup" />
              </Field>
              <div className="mt-4 space-y-3">
                {form.meeting_logs.map((m, i) => (
                  <div key={i} className="rounded-lg border border-border p-4 relative" data-testid={`meeting-log-card-${i}`}>
                    <button onClick={() => removeLog(i)} className="absolute top-3 right-3 text-xs text-destructive hover:underline">Remove</button>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Field label="Date"><Input type="date" value={m.date} onChange={(e) => setLog(i, "date", e.target.value)} /></Field>
                      <Field label="Logged By"><Input value={m.logged_by} onChange={(e) => setLog(i, "logged_by", e.target.value)} /></Field>
                      <div className="sm:col-span-2"><Field label="Meeting Summary / Notes"><Textarea value={m.summary} onChange={(e) => setLog(i, "summary", e.target.value)} rows={2} /></Field></div>
                      <div className="sm:col-span-2"><Field label="Next Step"><Input value={m.next_step} onChange={(e) => setLog(i, "next_step", e.target.value)} /></Field></div>
                    </div>
                  </div>
                ))}
                {form.meeting_logs.length < 4 && (
                  <Button variant="outline" size="sm" onClick={addMeetingLog} data-testid="add-meeting-log-button"><Plus className="h-4 w-4 mr-2" /> Add Meeting Log</Button>
                )}
              </div>
            </Section>
          </div>
        )}

        {!loading && (
          <div className="px-6 py-4 border-t border-border sticky bottom-0 bg-card flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} data-testid="cancel-button">Close</Button>
            <Button onClick={save} disabled={saving} data-testid="save-corporate-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : createMode ? "Create Corporate" : "Save Changes"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
